// Helpers for sending a campaign directly from the list (the three-dot "Send" action),
// without opening the editor. Maps a stored campaign record → the params the campaigns/sendCampaign
// store action expects (the same shape the modal's "Send Now" builds from its form state).
//
// KEEP IN SYNC WITH: src/store/campaigns.js sendCampaign(), and the campaign-service send handler.

// PatientList is stored as JSON; tolerate an array, a scalar, or junk.
function parsePatientList(raw) {
  try {
    const v = JSON.parse(raw == null ? 'null' : raw);
    if (Array.isArray(v)) return v;
    return v != null ? [v] : [];
  } catch (e) {
    return [];
  }
}

export function isAiCallCampaign(campaign) {
  return !!campaign && String(campaign.Type || '').toLowerCase() === 'ai_call';
}

// How many patients this campaign will reach (the resolved audience), for the confirm dialog.
export function campaignAudienceCount(campaign) {
  if (!campaign) return 0;
  const n = parsePatientList(campaign.PatientList).length;
  return n || campaign.TotalRecipients || 0;
}

// Which campaign id should the wizard PUT to — or null when it must POST a new one?
//
// The wizard's create-vs-update decision used to be `isEditing && campaign.CampaignIDForWeb` alone.
// When "Create & Send" creates the row and the SEND then fails (2026-09-02: a uk_campaign_email
// duplicate 500), the wizard stays open with `isEditing` still false — so the next click created a
// SIBLING campaign instead of re-sending the one that already exists. Once this session has created
// a campaign, every later save targets that same row.
//
// `createdCampaign` is whatever the create action returned: POST /campaigns answers with
// `campaignIDForWeb`, the list/GET shape uses `CampaignIDForWeb` — accept both.
export function resolveSaveTarget({ isEditing, campaign, createdCampaign } = {}) {
  if (isEditing && campaign?.CampaignIDForWeb) return campaign.CampaignIDForWeb;
  if (createdCampaign) {
    return createdCampaign.CampaignIDForWeb || createdCampaign.campaignIDForWeb || null;
  }
  return null;
}

// Build the campaigns/sendCampaign payload from a stored campaign record.
export function buildQuickSendParams(campaign) {
  let customFilters = null;
  try { customFilters = campaign.FilterConfig ? JSON.parse(campaign.FilterConfig) : null; } catch (e) { customFilters = null; }

  let removedPatNums = [];
  try { removedPatNums = (JSON.parse(campaign.WizardState || '{}').removedPatNums) || []; } catch (e) { removedPatNums = []; }

  const selectedList = parsePatientList(campaign.PatientList);

  return {
    campaignId: campaign.CampaignIDForWeb || campaign.CampaignID,
    campaignType: campaign.Type,
    customFilters: campaign.RecipientSource === 'customFilters' ? customFilters : null,
    selectedList: selectedList.length ? selectedList : null,
    smsContent: campaign.TextContent || null,
    filterSummary: campaign.FilterSummary || null,
    recipientSource: campaign.RecipientSource,
    removedPatNums,
    trackClicks: true,
    callFollowupTaskTypeID: isAiCallCampaign(campaign) ? campaign.CallFollowupTaskTypeID : null,
  };
}
